import { useQuery } from "@tanstack/react-query";
import { useStore } from "../../../helpers";

export interface SourceListParams {
  notebook_id: number;
  type?: string;
  parent_id: number | null;
  offset?: number;
  limit?: number;
  /** When true and parent_id is null, fetches root then all descendants so the full tree is returned. */
  fetchFullTree?: boolean;
}

export interface SourceListItem {
  id: number;
  name: string;
  parent_id: number | null;
  type: string;
  s3_key?: string;
  mime_type?: string;
  size_bytes?: number;
  tags?: string[];
  more?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface SourceListResponse {
  sources?: SourceListItem[];
  [key: string]: unknown;
}

export const useSourceList = (params: SourceListParams | null) => {
  const rootStore = useStore();
  const enabled =
    params != null &&
    params.notebook_id != null &&
    !Number.isNaN(Number(params.notebook_id));

  return useQuery({
    queryKey: [
      "tars",
      "source",
      "list",
      params?.notebook_id,
      params?.type ?? "",
      params?.parent_id,
      params?.offset ?? 0,
      params?.limit ?? 20,
      params?.fetchFullTree ?? false,
    ],
    queryFn: async () => {
      const fetchOne = async (
        parentId: number | null,
        offset = 0,
        limit = 100
      ): Promise<SourceListResponse> => {
        const payload: Record<string, number | string> = {
          notebook_id: params!.notebook_id,
          parent_id: parentId ?? null,
          offset,
          limit,
        };
        if (params!.type != null && params!.type !== "") {
          payload.type = params!.type;
        }
        const res = await rootStore.apiRequestHandler(
          "tars/source/list",
          payload,
          "Failed to fetch sources"
        );
        const body = (res.data ?? res) as SourceListResponse & { data?: unknown[]; items?: unknown[]; list?: unknown[] };
        const sources = body.sources ?? body.data ?? body.items ?? body.list;
        const list = Array.isArray(sources) ? sources : [];
        return {
          sources: list as SourceListItem[],
          total: body.total ?? list.length,
          offset: body.offset ?? 0,
          limit: body.limit ?? list.length,
        };
      };

      const fetchFullTree = params!.fetchFullTree === true && (params!.parent_id == null || params!.parent_id === 0);
      if (!fetchFullTree) {
        const res = await fetchOne(params!.parent_id ?? null, params!.offset ?? 0, params!.limit ?? 20);
        return res;
      }

      const PAGE_SIZE = 100;
      const all: SourceListItem[] = [];
      let folderIdsToFetch: number[] = [];
      const fetchedParentIds = new Set<number | null>([null, 0]);

      const MAX_PAGES = 50;
      const fetchAllForParent = async (parentId: number | null) => {
        let offset = 0;
        let page: SourceListItem[];
        let pageCount = 0;
        do {
          pageCount += 1;
          if (pageCount > MAX_PAGES) break;
          const res = await fetchOne(parentId, offset, PAGE_SIZE);
          page = res.sources ?? [];
          page.forEach((s) => {
            all.push(s);
            if (s.type === "folder") folderIdsToFetch.push(s.id);
          });
          offset += PAGE_SIZE;
        } while (page.length >= PAGE_SIZE);
      };

      await fetchAllForParent(null);

      const maxFolders = 500;
      let iterations = 0;
      while (folderIdsToFetch.length > 0 && iterations < maxFolders) {
        iterations += 1;
        const parentId = folderIdsToFetch.shift()!;
        if (fetchedParentIds.has(parentId)) continue;
        fetchedParentIds.add(parentId);
        await fetchAllForParent(parentId);
      }

      return { sources: all, total: all.length, offset: 0, limit: all.length };
    },
    enabled: enabled && params != null,
  });
};
